import { PAYMENT_METHOD, TRANSACTION_KIND } from '@/constants/wallet';

// Shared, i18n-free display resolution for LEDGER (non-pending, non-PoolShare) wallet
// transactions. Single source of truth consumed by both the transaction row
// (TransactionItem.vue) and the transaction detail sheet (TransactionDetailSheet.vue),
// so the two surfaces can never drift on title/icon/mode — the exact drift this feature
// was built to kill. Pending-request rows and the PoolShare ledger keep their own
// branches in TransactionItem (see poolShareTransaction.js).
//
// Every helper takes translated strings / i18n fns as arguments so the module stays pure
// and unit-testable in isolation.

// Icon + container tone per frozen `kind`. Mirrors the exhaustive BE enum
// (see ../../backend/_bmad-output/sync/briefs/2026-07-06-transaction-detail-attributes.md).
export const TRANSACTION_ICON_MAP = Object.freeze({
  [TRANSACTION_KIND.DEPOSIT]: { icon: 'arrow_down_circle_fill', tone: 'positive' },
  [TRANSACTION_KIND.WITHDRAWAL]: { icon: 'arrow_up_circle_fill', tone: 'negative' },
  [TRANSACTION_KIND.ORDER]: { icon: 'cart_fill', tone: 'negative' },
  [TRANSACTION_KIND.COMMISSION]: { icon: 'percent', tone: 'muted' },
  [TRANSACTION_KIND.POOL_CREDIT]: { icon: 'building_2_fill', tone: 'positive' },
  [TRANSACTION_KIND.POOL_SHARE]: { icon: 'gift_fill', tone: 'positive' },
  [TRANSACTION_KIND.OPENING_BALANCE]: { icon: 'circle_grid_3x3_fill', tone: 'muted' },
  [TRANSACTION_KIND.INVOICE_HOLD]: { icon: 'doc_text_fill', tone: 'negative' },
  [TRANSACTION_KIND.TOP_UP]: { icon: 'arrow_down_circle_fill', tone: 'positive' },
  [TRANSACTION_KIND.SUPPLIER_DIRECT_DEPOSIT]: { icon: 'bookmark', tone: 'warn' },
  [TRANSACTION_KIND.INVOICE_SETTLEMENT]: { icon: 'doc_text_fill', tone: 'negative' },
  // Direction-neutral by design — this row can be a credit OR a debit, so a
  // positive/negative tone would be wrong half the time.
  [TRANSACTION_KIND.INVOICE_SETTLEMENT_ADJUSTMENT]: {
    icon: 'doc_text_fill',
    tone: 'muted',
  },
  [TRANSACTION_KIND.OUT_OF_STOCK_REFUND]: {
    icon: 'arrow_uturn_left_circle_fill',
    tone: 'positive',
  },
  [TRANSACTION_KIND.WRITE_OFF_REFUND]: { icon: 'arrow_uturn_left_circle_fill', tone: 'positive' },
  [TRANSACTION_KIND.GOODWILL_REFUND]: { icon: 'arrow_uturn_left_circle_fill', tone: 'positive' },
  // Wallet-to-wallet correction, NOT a loss — neutral tone and a transfer glyph
  // so the negative amount never reads as money taken from the member.
  [TRANSACTION_KIND.GOODWILL_REFUND_REVERSAL]: {
    icon: 'arrow_right_arrow_left_circle_fill',
    tone: 'muted',
  },
  [TRANSACTION_KIND.WALLET_REQUEST_REVERSAL]: {
    icon: 'arrow_uturn_left_circle_fill',
    tone: 'muted',
  },
  [TRANSACTION_KIND.WALLET_REQUEST_REAPPLY]: { icon: 'arrow_clockwise_circle_fill', tone: 'muted' },
  [TRANSACTION_KIND.POOLA_PURCHASE_CASH]: { icon: 'cart_fill', tone: 'negative' },
  [TRANSACTION_KIND.POOLA_REFUND]: { icon: 'arrow_uturn_left_circle_fill', tone: 'positive' },
  // Money in, like a refund — but for a piece of hers that SOLD, so a price tag rather
  // than the return arrow.
  [TRANSACTION_KIND.TADBEESA_PAYOUT]: { icon: 'tag_fill', tone: 'positive' },
  [TRANSACTION_KIND.SUPPLIER_COLLECTION_PAYOUT]: { icon: 'tray_full_fill', tone: 'positive' },
  [TRANSACTION_KIND.POOLSHARE_EARNED]: { icon: 'bag_fill', tone: 'positive' },
  [TRANSACTION_KIND.POOLSHARE_CONSUMED]: { icon: 'arrow_up_circle_fill', tone: 'negative' },
  [TRANSACTION_KIND.POOLCREDIT_GENERATED]: { icon: 'building_2_fill', tone: 'positive' },
  [TRANSACTION_KIND.POOLCREDIT_APPLIED]: { icon: 'building_2_fill', tone: 'negative' },
  [TRANSACTION_KIND.POOLCREDIT_GRANT]: { icon: 'building_2_fill', tone: 'positive' },
  [TRANSACTION_KIND.POOLCREDIT_PAYOUT]: { icon: 'building_2_fill', tone: 'negative' },
  [TRANSACTION_KIND.POOLCREDIT_EARNING]: { icon: 'bag_fill', tone: 'positive' },
  [TRANSACTION_KIND.POOLCREDIT_EXPIRY]: { icon: 'clock_fill', tone: 'muted' },
  [TRANSACTION_KIND.POOLCREDIT_POOLA_REVERSAL]: {
    icon: 'arrow_uturn_left_circle_fill',
    tone: 'muted',
  },
  [TRANSACTION_KIND.POOLCREDIT_ADMIN_REVERSAL]: {
    icon: 'arrow_uturn_left_circle_fill',
    tone: 'muted',
  },
});

const FALLBACK_ICON_META = Object.freeze({ icon: 'circle_fill', tone: 'muted' });

// Never echo a raw i18n key path — any unmapped/empty/null `kind` falls back to the
// generic transaction label.
const GENERIC_TITLE_KEY = 'wallet.transactions.types.generic';

// Pool_credit kinds whose title folds in the supplier name («رصيد من {supplier}» /
// «ربح من {supplier}»). Each has a `wallet.transactions.types.<kind>` key that takes a
// {supplier} param, and degrades to the plain `pool_credit` label when the name is absent.
const SUPPLIER_SCOPED_KINDS = new Set([
  TRANSACTION_KIND.POOLCREDIT_GENERATED,
  TRANSACTION_KIND.POOLCREDIT_GRANT,
  TRANSACTION_KIND.POOLCREDIT_EARNING,
]);

// Pool_credit kinds sourced from a completed «بولة» (Poola) offer. When the
// backend resolves both the offer + deal titles (TransactionPoolaContextResolver),
// the row names that Poola instead of the generic supplier label — «بولكريديت
// {offer} من {deal}». A manual/admin grant carries no Poola link → both titles
// null → the kind falls through to its supplier-scoped / plain label.
const POOLA_CONTEXT_TITLE_KEY = Object.freeze({
  [TRANSACTION_KIND.POOLCREDIT_GRANT]: 'wallet.transactions.types.poolcredit_grant_context',
  [TRANSACTION_KIND.POOLCREDIT_POOLA_REVERSAL]:
    'wallet.transactions.types.poolcredit_poola_reversal_context',
});

// Kinds that render in the detail sheet's INVOICE mode (order box + view-invoice CTA);
// everything else renders in STANDARD mode.
export const INVOICE_KINDS = Object.freeze([
  TRANSACTION_KIND.INVOICE_HOLD,
  TRANSACTION_KIND.INVOICE_SETTLEMENT,
]);

// Refund kinds whose sheet note must carry a CANNED reason label, because the backend
// serves no free text for them — the canned line is the only "why" the member gets. A
// write-off's title now names the item («مرتجع {item}»), but that says WHAT came back,
// not why, so its note stays.
// Maps kind → i18n suffix under detail.refundReason.*.
//
// GOODWILL_REFUND is deliberately ABSENT. The backend now serves a real, admin-authored
// `reason` on every goodwill refund (the Filament field is `->required()`), so the canned
// line was both redundant and wrongly framed — «مرتجع بحسن نية من بوليت» ("a goodwill
// refund from PoolIt") casts money the member is owed as a favour, which cuts against the
// partner-not-charity voice. The member's own reason says it better. Retired 2026-07-29.
const REFUND_REASON_KIND = Object.freeze({
  [TRANSACTION_KIND.OUT_OF_STOCK_REFUND]: 'out_of_stock_refund',
  [TRANSACTION_KIND.WRITE_OFF_REFUND]: 'write_off_refund',
});

// Refund kinds whose row leads with the admin's own free-text `reason` (locked answer
// L-D): the reason names what the money is for in her words, where the kind label only
// says «مرتجعات» / «تعديل الفاتورة». The backend serves `reason` for exactly these kinds
// (TransactionResource REASON_META_KEY). invoice_settlement_adjustment stays
// direction-neutral — the signed amount says which way the money moved.
const REASON_TITLE_KINDS = new Set([
  TRANSACTION_KIND.GOODWILL_REFUND,
  TRANSACTION_KIND.INVOICE_SETTLEMENT_ADJUSTMENT,
]);

/**
 * @param {string} kind
 * @returns {boolean} true for a refund kind whose title leads with the admin's reason
 *   (and whose row footer is therefore date · carton · shop, never the reason again).
 */
export function isReasonLedKind(kind) {
  return REASON_TITLE_KINDS.has(kind);
}

// A write-off refund names the item it paid back for («مرتجع {item}»), so — like the
// reason-led kinds — its small line is only where it hangs: date · carton · shop.
// A «تدبيسة» payout names her item the same way; the backend sends it no carton (the
// buyer's is never hers to see), so its line is date · shop — «تدبيسة النادي».
const CARTON_SHOP_FOOTER_KINDS = new Set([
  ...REASON_TITLE_KINDS,
  TRANSACTION_KIND.WRITE_OFF_REFUND,
  TRANSACTION_KIND.TADBEESA_PAYOUT,
]);

/**
 * @param {string} kind
 * @returns {boolean} true when the row's footer is date · carton · shop (the title
 *   already says what the money was for).
 */
export function usesCartonShopFooter(kind) {
  return CARTON_SHOP_FOOTER_KINDS.has(kind);
}

/**
 * A title that names an item: `<key>` takes {item}, and `<key>_qty` adds «×{qty}» —
 * used only when there is more than one.
 * @param {object} args
 * @param {string} args.key      i18n key of the {item}-only form
 * @param {string} args.item
 * @param {number|null} [args.itemQty]
 * @param {(key: string, named?: object) => string} args.t
 * @returns {string}
 */
function itemTitle({ key, item, itemQty, t }) {
  const qty = Math.trunc(Number(itemQty));
  if (!(qty > 1)) return t(key, { item });
  // A bare «×2» has no strong direction, so an RTL line reorders it to «2×». The LRM in
  // front makes the fragment one left-to-right run that stays «×2» at the line's end.
  return t(`${key}_qty`, { item, qty: `\u200E×${qty}` });
}

/**
 * The written-off item a refund paid back for: «مرتجع {item}», with «×{qty}» only when
 * more than one. Null when the backend could not name the item (the caller then falls
 * back to the kind label).
 * @param {object} args
 * @param {string|null} [args.itemName]
 * @param {number|null} [args.itemQty]
 * @param {(key: string, named?: object) => string} args.t
 * @returns {string|null}
 */
function resolveWrittenOffItemTitle({ itemName, itemQty, t }) {
  const item = filledString(itemName);
  if (!item) return null;
  return itemTitle({ key: 'wallet.transactions.types.write_off_refund_item', item, itemQty, t });
}

/**
 * Her own «تدبيسة» piece that sold: «تدبيسة: {item}», with «×{qty}» only when more than
 * one — the quantity is HER pieces on the line, as the backend counts them. An item the
 * backend could not name still gets the row, under the plain unnamed-item word.
 * @param {object} args
 * @param {string|null} [args.itemName]
 * @param {number|null} [args.itemQty]
 * @param {(key: string, named?: object) => string} args.t
 * @returns {string}
 */
function resolveTadbeesaSaleTitle({ itemName, itemQty, t }) {
  const item =
    filledString(itemName) ?? t('wallet.transactions.types.tadbeesa_payout_unnamed_item');
  return itemTitle({ key: 'wallet.transactions.types.tadbeesa_payout_item', item, itemQty, t });
}

/**
 * True when the ledger title is the admin's `reason` — so no other surface repeats it.
 * @param {object} args
 * @param {string} args.kind
 * @param {string|null} [args.reason]
 * @returns {boolean}
 */
export function ledgerTitleUsesReason({ kind, reason }) {
  return isReasonLedKind(kind) && typeof reason === 'string' && reason.trim() !== '';
}

// Top-ups and withdrawals name the payment account they went through («إيداع من
// انستاباي» / «سحب إلى فودافون كاش»). The label rides on the row itself
// (`paymentAccountLabel`, locked answer L-B) — NEVER resolve it from /payment-accounts:
// that endpoint lists active accounts only, and a retired account still owns hundreds
// of historical rows that would go blank.
const ACCOUNT_TITLE_KEY = Object.freeze({
  [TRANSACTION_KIND.DEPOSIT]: 'wallet.transactions.types.deposit_via',
  [TRANSACTION_KIND.WITHDRAWAL]: 'wallet.transactions.types.withdrawal_via',
});

function filledString(value) {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/**
 * Icon + tone for a ledger transaction kind.
 * @param {string} kind
 * @returns {{icon: string, tone: string}}
 */
export function resolveLedgerIconMeta(kind) {
  return TRANSACTION_ICON_MAP[kind] || FALLBACK_ICON_META;
}

/**
 * Legible, entity-aware title for a ledger transaction. Folds the supplier name into
 * supplier-scoped kinds; NEVER returns a raw key path or a literal "{supplier}".
 * @param {object} args
 * @param {string} args.kind
 * @param {string|null} [args.supplierName]
 * @param {string|null} [args.poolaOfferTitle]  Poola offer/item title (grant/reversal only)
 * @param {string|null} [args.poolaDealTitle]   Poola deal title (grant/reversal only)
 * @param {string|null} [args.reason]           admin's free-text reason (refund kinds lead with it)
 * @param {string|null} [args.paymentAccountLabel] account a deposit/withdrawal went through
 * @param {string|null} [args.method]           payment method (`meta.method`); 'cash' names a cash deposit
 * @param {string|null} [args.orderBoxItemName] the item's name (write_off_refund, tadbeesa_payout)
 * @param {number|null} [args.orderBoxItemQty]  its quantity — on a tadbeesa_payout, her pieces
 * @param {(key: string, named?: object) => string} args.t   vue-i18n translate
 * @param {(key: string) => boolean} args.te                 vue-i18n key-exists check
 * @returns {string}
 */
export function resolveLedgerTitle({
  kind,
  supplierName,
  poolaOfferTitle,
  poolaDealTitle,
  reason,
  paymentAccountLabel,
  method,
  orderBoxItemName,
  orderBoxItemQty,
  t,
  te,
}) {
  if (ledgerTitleUsesReason({ kind, reason })) return reason.trim();
  if (kind === TRANSACTION_KIND.WRITE_OFF_REFUND) {
    const writtenOff = resolveWrittenOffItemTitle({
      itemName: orderBoxItemName,
      itemQty: orderBoxItemQty,
      t,
    });
    if (writtenOff) return writtenOff;
  }
  if (kind === TRANSACTION_KIND.TADBEESA_PAYOUT) {
    return resolveTadbeesaSaleTitle({
      itemName: orderBoxItemName,
      itemQty: orderBoxItemQty,
      t,
    });
  }
  const account = filledString(paymentAccountLabel);
  if (account && ACCOUNT_TITLE_KEY[kind]) {
    return t(ACCOUNT_TITLE_KEY[kind], { account });
  }
  if (kind === TRANSACTION_KIND.DEPOSIT && method === PAYMENT_METHOD.CASH) {
    return t('wallet.transactions.types.deposit_cash');
  }
  if (kind === TRANSACTION_KIND.SUPPLIER_DIRECT_DEPOSIT) {
    return supplierName
      ? t('wallet.transactions.types.supplier_direct_deposit', { supplier: supplierName })
      : t('wallet.transactions.types.supplier_direct_deposit', { supplier: '' }).trim();
  }
  // Poola-sourced credit: name the offer + deal («بولكريديت {offer} من {deal}»).
  // Only when BOTH titles resolved — a manual/admin grant has neither, so it
  // falls through to the supplier-scoped label below.
  const poolaKey = POOLA_CONTEXT_TITLE_KEY[kind];
  if (poolaKey && poolaOfferTitle && poolaDealTitle) {
    return t(poolaKey, { offer: poolaOfferTitle, deal: poolaDealTitle });
  }
  // Supplier-scoped pool_credit kinds fold the supplier name into the title, and fall
  // back to a plain "supplier credit" label when the name is absent (never a literal
  // "{supplier}").
  if (SUPPLIER_SCOPED_KINDS.has(kind)) {
    const supplierKey = `wallet.transactions.types.${kind}`;
    return supplierName && te(supplierKey)
      ? t(supplierKey, { supplier: supplierName })
      : t('wallet.transactions.types.pool_credit');
  }
  const key = kind ? `wallet.transactions.types.${kind}` : null;
  return key && te(key) ? t(key) : t(GENERIC_TITLE_KEY);
}

/**
 * @param {string} kind
 * @returns {boolean} true when the sheet should render in INVOICE mode.
 */
export function isInvoiceKind(kind) {
  return INVOICE_KINDS.includes(kind);
}

/**
 * Resolve the concrete refund-reason note for the detail sheet. Prefers a supplier-aware
 * variant when a supplier name is present and that key exists. Returns null for any
 * non-refund kind (the sheet then falls back to the member's own note, if any).
 * @param {object} args
 * @param {string} args.kind
 * @param {string|null} [args.supplierName]
 * @param {(key: string, named?: object) => string} args.t
 * @param {(key: string) => boolean} args.te
 * @returns {string|null}
 */
export function resolveRefundReasonNote({ kind, supplierName, t, te }) {
  const suffix = REFUND_REASON_KIND[kind];
  if (!suffix) return null;
  const baseKey = `wallet.transactions.detail.refundReason.${suffix}`;
  const withSupplierKey = `${baseKey}_withSupplier`;
  if (supplierName && te(withSupplierKey)) {
    return t(withSupplierKey, { supplier: supplierName });
  }
  return te(baseKey) ? t(baseKey) : null;
}

/**
 * What she sent, for the small muted line under a row's big "landed" amount — only when a
 * fee was actually taken (the 1% mobile-wallet cut). A fee-free deposit (InstaPay) sent
 * exactly what landed, so a second number would only be noise: returns null.
 * @param {object} args
 * @param {number|null|undefined} args.grossCents  what she sent (pre-fee)
 * @param {number|null|undefined} args.feeCents    the provider's cut
 * @returns {number|null} the gross to render, or null for no hint
 */
export function resolveFeeHint({ grossCents, feeCents }) {
  if (!(Number(feeCents) > 0)) return null;
  const gross = Number(grossCents);
  return Number.isFinite(gross) && gross > 0 ? gross : null;
}

/**
 * The fee a payment account will take on a deposit, BEFORE she pays (locked answer L-E).
 * Mirrors the backend exactly — ../backend/app/Models/Financial/PaymentAccount.php
 * commissionCents(): intdiv(gross × bp, 10000), then floored to a whole pound
 * (intdiv(raw, 100) × 100). That is the rounding the posted deposit uses
 * (WalletService::deposit), so the figure shown here is the one that lands.
 * e.g. 1,000 EGP at 100 bp → fee 10, lands 990; 615 EGP → fee 6, lands 609.
 * @param {object} args
 * @param {number} args.grossCents   what she will send
 * @param {number} args.basisPoints  the account's commissionBasisPoints (100 = 1%)
 * @returns {{feeCents: number, netCents: number}|null} null when no fee would be taken
 */
export function resolveDepositFee({ grossCents, basisPoints }) {
  const gross = Math.trunc(Number(grossCents));
  const bp = Math.trunc(Number(basisPoints));
  if (!(gross > 0) || !(bp > 0)) return null;
  const rawCents = Math.trunc((gross * bp) / 10000);
  const feeCents = Math.trunc(rawCents / 100) * 100;
  if (feeCents <= 0) return null;
  return { feeCents, netCents: gross - feeCents };
}
